// A BUTTON HAS NO ROW TO RENAME — the third way a rename can save and change
// nothing (2026-10-23, customer complaint: Satish Ahuja, Vani room, "window
// side"). The other two are in CLAUDE.md; both of them assume the row exists.
//
// Device 9327303 (DeviceID 96, 7 outputs) had **no `OutputDetails` rows at
// all**, while its three siblings in the same room had theirs. A rename is
//
//     UPDATE OutputDetails SET OutputName = ? WHERE UniqueNumber = ? AND Output = ?
//
// so with no row it matches nothing. ⚠️ AND THE SERVER STILL ANSWERS HTTP 200 —
// measured on the live API:
//
//     {"affectedRows":0,"info":"Rows matched: 0  Changed: 0","changedRows":0}
//
// Every caller treated that as saved. The offline cache patch then found no row
// to patch either, so the new name appeared NOWHERE — no error, nothing
// changed, on both shells, for ever. 49 of 464 devices on the live estate had
// no output rows and 9 more were missing some.
//
// ⚠️ THE ROW IS CREATED EXACTLY AS THE BACKEND CREATES IT — four values
// (UniqueNumber, Output, OutputName, DeviceInstalledId), every other column
// left to the database default. That is what `CheckForMissingOutputDetails`
// (asynccontrollers.js) and `freshPiece` both do; a fifth value invented here
// would be a setting nobody chose.
//
// This file is PURE — no I/O, so the rules are testable. The write lives in
// core/api/api.js (`writeOutputRow`), which is where `post` is.

/** `O_<uno>_<n>` — the name the backend gives a row it creates itself. */
export const freshOutputName = (uno, output) => `O_${uno}_${output}`;

/**
 * Did this write reach no row at all?
 *
 * ⚠️ TRUE ONLY ON A CLEAR "ROWS MATCHED: 0". Anything else — an array, a
 * `{status:'500'}` body, a reply with no `affectedRows` at all — is NOT this
 * case, and must not be turned into an insert: creating a row because a reply
 * was merely unfamiliar would invent data on every backend change.
 */
export function wroteNothing(res) {
  const body = res && typeof res === 'object' && 'data' in res ? res.data : res;
  if (!body || typeof body !== 'object' || Array.isArray(body)) return false;
  if (String(body.status) === '500') return false;          // the server's own failure shape
  if (!Object.prototype.hasOwnProperty.call(body, 'affectedRows')) return false;
  return Number(body.affectedRows) === 0;
}

/** Which output numbers 1..expected have no row yet. Pure. */
export function missingOutputs(expected, rows) {
  const n = Math.max(0, Number(expected) || 0);
  const have = new Set((rows || []).map((r) => Number(r.Output ?? r.output_index)));
  const out = [];
  for (let o = 1; o <= n; o += 1) if (!have.has(o)) out.push(o);
  return out;
}

// ── Which types the repair tool TICKS for you ─────────────────────────────
// ⚠️ A MISSING ROW IS NOT ALWAYS A FAULT. A fan (DeviceID 53) keeps its state
// in `DevicesInstalled.Status` ONLY — the backend says so in as many words
// ("the fan is a single output, so it does not need a per-output OutputDetails
// row") — and the WWH lights, the AC touch and the BLE controllers have run
// for years without rows. Their missing rows are listed but NOT ticked: the
// operator decides, and nothing is written on a guess.
//
// A switch bank, a glass plate and a curtain are different: their per-button
// NAME lives in that row, which is the whole complaint this file exists for.
export const SKIP_BY_DEFAULT = [25, 53, 60, 90, 100];

export const repairByDefault = (deviceId) => !SKIP_BY_DEFAULT.includes(Number(deviceId));

/**
 * The server's "missing output rows" answer -> what the tool shows.
 * Pure: rows in, rows out.
 *
 * `rows` carry UniqueNumber, ID (DeviceInstalledId), DeviceID, TypeName,
 * DeviceName, LocationName, RoomName, Expected, Actual.
 * -> [{ ...row, missing: [n], ticked }]
 */
export function repairPlan(rows) {
  // ⚠️ TWO INSTALL ROWS CAN SHARE ONE UNIQUE NUMBER. Found on the live estate
  // 2026-10-24: 1128094 is DevicesInstalled 815 AND 1502 (same room, same
  // owner, one with 3 user mappings and one with none), and 14937839 likewise.
  // The missing-rows query groups by did.ID, so such a device appears TWICE.
  // Output rows are keyed by UniqueNumber + Output, so repairing both would be
  // one upsert fighting another over the DeviceInstalledId. Collapse them here,
  // keeping the row that looks installed (placed, and the most rows already),
  // and carry the other id so the screen can say the device is duplicated.
  const first = new Map();
  (rows || []).forEach((r) => {
    const key = String(r.UniqueNumber);
    const kept = first.get(key);
    if (!kept) { first.set(key, { ...r, duplicateIds: [] }); return; }
    const better = (Number(r.Actual) || 0) > (Number(kept.Actual) || 0)
      || (!kept.LocationName && !!r.LocationName);
    if (better) {
      first.set(key, { ...r, duplicateIds: [...kept.duplicateIds, kept.ID] });
    } else {
      kept.duplicateIds = [...kept.duplicateIds, r.ID];
    }
  });
  return [...first.values()]
    .map((r) => {
      const expected = Math.max(0, Number(r.Expected) || 0);
      const actual = Math.max(0, Number(r.Actual) || 0);
      // The server counts rows, not which ones; a gap of N is outputs
      // actual+1..expected only when the rows are the first N. `missing` is
      // filled in exactly once the rows are read, per device, before writing.
      return {
        ...r,
        expected,
        actual,
        gap: Math.max(0, expected - actual),
        ticked: repairByDefault(r.DeviceID),
      };
    })
    .filter((r) => r.gap > 0)
    .sort((a, b) => String(a.LocationName || '').localeCompare(String(b.LocationName || ''))
      || String(a.UniqueNumber).localeCompare(String(b.UniqueNumber)));
}

/** "6 devices · 31 button rows" — what the confirm says BEFORE the write. */
export function repairSummary(plan) {
  const picked = (plan || []).filter((r) => r.ticked);
  const rows = picked.reduce((n, r) => n + (r.missing ? r.missing.length : r.gap), 0);
  return {
    devices: picked.length,
    rows,
    text: picked.length
      ? `${picked.length} device${picked.length === 1 ? '' : 's'} · ${rows} button row${rows === 1 ? '' : 's'}`
      : 'nothing selected',
  };
}

const outputRows = {
  wroteNothing, missingOutputs, repairPlan, repairSummary, freshOutputName,
};
export default outputRows;
